import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router } from "@angular/router";
import { of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

import { AuthModel, EXPLICIT_SELECT_ORGANISATION } from "@intocare/auth-data";
import { AWSHttpClient } from "@intocare/aws";
import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";
import { OrganisationModel } from "@intocare/organisation/data";

@Injectable()
export class OrganisationGuard implements CanActivate {
	private authModel = inject(AuthModel);
	private router = inject(Router);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);
	private organisationModel = inject(OrganisationModel);
	private awsHttpClient = inject(AWSHttpClient);

	canActivate(route: ActivatedRouteSnapshot) {
		const routeOrganisation = `${route.root.firstChild.params.org}`;
		const currentOrganisation = localStorage.getItem("currentOrganisation");
		if (routeOrganisation !== currentOrganisation) {
			return this.authModel.bootstrapUserPool(routeOrganisation).pipe(
				map(() => {
					localStorage.setItem("currentOrganisation", routeOrganisation);
					this.updateOrganisationInfo(routeOrganisation);
					return true;
				}),
				catchError(() => {
					if (!currentOrganisation) {
						return of(
							this.router.createUrlTree(["select-organisation"], {
								queryParams: { [EXPLICIT_SELECT_ORGANISATION]: 1 },
							}),
						);
					}
					return this.authModel.bootstrapUserPool(currentOrganisation).pipe(
						switchMap(() => {
							return this.authModel.logOut(false, { redirect: false });
						}),
						map(() => {
							return this.router.createUrlTree(["select-organisation"], {
								queryParams: { [EXPLICIT_SELECT_ORGANISATION]: 1 },
							});
						}),
					);
				}),
			);
		}
		return this.organisationModel.organisationName$.pipe(
			take(1),
			map((organisation) => {
				if (!organisation) {
					this.updateOrganisationInfo(routeOrganisation);
				}
				return true;
			}),
		);
	}

	updateOrganisationInfo(organisation: string) {
		const url = this.environment.API.url.replace(/\/$/, "");
		this.organisationModel.setOrganisationName(organisation);
		this.awsHttpClient.setBaseUrl(`${url}/${organisation}.hq`);
	}
}
